import React, { useCallback, useEffect, useState } from 'react';
import { ButtonComponent } from '@syncfusion/ej2-react-buttons';
import { InPlaceEditorComponent } from '@syncfusion/ej2-react-inplace-editor';

declare var InovxLoader: any;

interface ControlPointsProps {
    editMode?: boolean;
    gpsControlPoints?: IGpsControlPoint[] | null;
    onControlPointChange?: (data: IGpsControlPoint[]) => void;
}

const DEFAULT_POINTS: IGpsControlPoint[] = Array(3).fill({
    Wgs84Lat: 0,
    Wgs84Long: 0,
    Wgs84Alt: 0,
    FacilityN: 0,
    FacilityE: 0,
    FacilityU: 0,
});

const ControlPoints: React.FC<ControlPointsProps> = ({ editMode, onControlPointChange, gpsControlPoints }) => {
    const [controlPoints, setControlPoints] = useState<IGpsControlPoint[]>([]);
    const editorRefs = React.useRef<Record<string, InPlaceEditorComponent | null>>({});

    useEffect(() => {
        setControlPoints(gpsControlPoints?.length ? gpsControlPoints : DEFAULT_POINTS);
    }, [gpsControlPoints]);

    const updateValue = useCallback((index: number, key: keyof IGpsControlPoint, value: number) => {
        if (!editMode) return;

        const next = [...controlPoints];
        next[index] = { ...next[index], [key]: value };
        setControlPoints(next);
        onControlPointChange?.(next);
    }, [controlPoints, editMode, onControlPointChange]);

    const addRow = () => {
        const newRecord: IGpsControlPoint = {
            Wgs84Lat: 0,
            Wgs84Long: 0,
            Wgs84Alt: 0,
            FacilityN: 0,
            FacilityE: 0,
            FacilityU: 0,
        };

        const next = [...controlPoints, newRecord];
        setControlPoints(next);
        onControlPointChange?.(next);
    };

    const deleteRow = (index: number) => {
        if (!editMode) return;

        const next = controlPoints.filter((_, pointIndex) => pointIndex !== index);
        setControlPoints(next);
        onControlPointChange?.(next);
    };

    const getControlPointLabel = (index: number) => InovxLoader.t('facility:hierarchy.controlPointLabel').replace('__index__', `${index + 1}`);

    const renderGPSControlRow = (index: number, row: IGpsControlPoint) => (
        <div className="gps-row" key={index}>
            <div style={{ width: '10%' }}>
                <label className="primary-label">
                    {getControlPointLabel(index)}
                </label>
                <div style={{ height: '27px' }}>&nbsp;</div>
            </div>
            <div style={{ width: '13%' }}>
                <div className="gps-title font-bold">{InovxLoader.t('facility:hierarchy.wgs84Latitude')}</div>
                    <InPlaceNumericEditor key={`lat-${index}-${row.Wgs84Lat}`} value={row.Wgs84Lat} editable={!!editMode} allowZero={false} index={index} field="Wgs84Lat" editorRefs={editorRefs} onChange={val => updateValue(index, 'Wgs84Lat', val)} />
            </div>
            <div style={{ width: '13%' }}>
                <div className="gps-title font-bold">{InovxLoader.t('facility:hierarchy.longitude')}</div>
                <InPlaceNumericEditor key={`lon-${index}-${row.Wgs84Long}`} value={row.Wgs84Long} editable={!!editMode} allowZero={false} index={index} field="Wgs84Long" editorRefs={editorRefs} onChange={val => updateValue(index, 'Wgs84Long', val)} />
            </div>
            <div style={{ width: '13%' }}>
                <div className="gps-title font-bold">{InovxLoader.t('facility:hierarchy.altitude')}</div>
                <InPlaceNumericEditor key={`alt-${index}-${row.Wgs84Alt}`} value={row.Wgs84Alt} editable={!!editMode} allowZero={false} index={index} field="Wgs84Alt" editorRefs={editorRefs} onChange={val => updateValue(index, 'Wgs84Alt', val)} />
            </div>
            <div style={{ width: '13%' }}>
                <div className="gps-title font-bold">{InovxLoader.t('facility:hierarchy.facilityNorth')}</div>
                <InPlaceNumericEditor key={`facility-n-${index}-${row.FacilityN}`} value={row.FacilityN} editable={!!editMode} allowZero={true} index={index} field="FacilityN" editorRefs={editorRefs} onChange={val => updateValue(index, 'FacilityN', val)} />
            </div>
            <div style={{ width: '13%' }}>
                <div className="gps-title font-bold">{InovxLoader.t('facility:hierarchy.facilityEast')}</div>
                <InPlaceNumericEditor key={`facility-e-${index}-${row.FacilityE}`} value={row.FacilityE} editable={!!editMode} allowZero={true} index={index} field="FacilityE" editorRefs={editorRefs} onChange={val => updateValue(index, 'FacilityE', val)} />
            </div>
            <div style={{ width: '13%' }}>
                <div className="gps-title font-bold">{InovxLoader.t('facility:hierarchy.facilityUp')}</div>
                <InPlaceNumericEditor key={`facility-u-${index}-${row.FacilityU}`} value={row.FacilityU} editable={!!editMode} allowZero={true} index={index} field="FacilityU" editorRefs={editorRefs} onChange={val => updateValue(index, 'FacilityU', val)} />
            </div>
            <div style={{ width: '5%' }}>
                <div className="gps-title">&nbsp;</div>
                <div className="d-flex">
                    {InovxLoader.t('facility:hierarchy.metersShort')} {editMode && index > 2 && (
                        <span
                            className="e-icons e-delete"
                            title={InovxLoader.t('facility:form.deleteControlPointTitle')}
                            style={{ cursor: 'pointer', marginLeft: 15 }}
                            onClick={() => deleteRow(index)}
                        />
                    )}
                </div>
            </div>
        </div>
    );

    return (
        <div className="gps-container">
            <div className="gps-table">
                {controlPoints.map((row, index) => renderGPSControlRow(index, row))}
            </div>
            {editMode && (
                <div style={{ marginTop: 10 }}>
                    <ButtonComponent title={InovxLoader.t('facility:form.addControlPointTitle')} iconCss="e-icons e-plus" cssClass="e-round e-primary" onClick={addRow} />
                </div>
            )}
        </div>
    );
};

interface InPlaceNumericEditorProps {
    value: number;
    editable: boolean;
    allowZero: boolean;
    index: number;
    field: keyof IGpsControlPoint;
    editorRefs: React.MutableRefObject<Record<string, InPlaceEditorComponent | null>>;
    onChange: (value: number) => void;
}

const InPlaceNumericEditor: React.FC<InPlaceNumericEditorProps> = ({ value, editable, allowZero, index, field, editorRefs, onChange }) => (
    <InPlaceEditorComponent
        ref={el => {
            editorRefs.current[`${index}-${field}`] = el;
        }}
        mode="Popup"
        type="Numeric"
        value={value}
        showButtons={true}
        submitOnEnter={true}
        editableOn="Click"
        actionOnBlur="Submit"
        cssClass="inplace-numeric-editor"
        model={{ decimals: 10, format: '##########.##########', showSpinButton: false, enabled: editable }}
        actionBegin={(e: any) => {
            const numericValue = Number(e.data?.value);

            if (e.data?.value === '' || e.data?.value === null) {
                e.cancel = true;
            }

            if (!allowZero && numericValue === 0) {
                e.cancel = true;
            }
        }}
        actionSuccess={(e: any) => {
            if (e.value !== undefined) {
                onChange(Number(e.value));
            }
        }}
        cancelClick={() => {
            const ref = editorRefs.current[`${index}-${field}`];
            if (ref) {
                ref.value = value;
            }
        }}
        disabled={!editable}
    />
);

export default ControlPoints;
